import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, LocateFixed, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";
import {
    resolveWhatsAppDigits,
    whatsappUrl,
    bikeInquiryMessage,
} from "@/lib/contact";
import { trackLeadConversion } from "@/lib/seo";
import { loadStoredLocation, requestUserLocation } from "@/lib/geo";
import {
    VISIT_PURPOSES,
    intentToPurpose,
    loadVisitor,
    saveVisitor,
} from "@/lib/visitor";
import { selectOption } from "@/lib/selectOption";

const TITLES = {
    test_ride: "Book a Test Ride",
    pre_book: "Register Your Interest",
    price_drop: "Get Price Updates",
    whatsapp_inquiry: "Get On-Road Quote",
    loan_inquiry: "Enquire About EMI",
    compare_help: "Help Me Decide",
    general: "Request a Callback",
};

const LeadModal = ({ open, onOpenChange, intent = "general", bike, note = "", defaultCity = "" }) => {
    const { settings } = useSettings();
    const [form, setForm] = useState({ name: "", phone: "", purpose: "general", email: "", city: defaultCity });
    const [showMore, setShowMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [locating, setLocating] = useState(false);
    const [coords, setCoords] = useState(() => loadStoredLocation());

    useEffect(() => {
        if (!open) return;
        const saved = loadVisitor();
        setForm({
            name: saved?.name || "",
            phone: saved?.phone || "",
            purpose: intentToPurpose(intent) || saved?.purpose || "general",
            email: saved?.email || "",
            city: saved?.city || defaultCity,
        });
        setCoords(loadStoredLocation());
        setShowMore(Boolean(saved?.email || saved?.city || loadStoredLocation()));
    }, [open, defaultCity, intent]);

    const useLocation = async () => {
        setLocating(true);
        try {
            const loc = await requestUserLocation();
            setCoords(loc);
            saveVisitor({ lat: loc.lat, lng: loc.lng });
            toast.success("Location saved — we'll match the nearest outlet.");
        } catch (e) {
            toast.error(e.message || "Could not get location.");
        } finally {
            setLocating(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        const phone = form.phone.trim();
        if (!form.name.trim() || !phone) {
            toast.error("Name and phone are required");
            return;
        }
        if (!form.purpose) {
            toast.error("Please tell us why you're visiting");
            return;
        }
        if (phone.replace(/\D/g, "").length < 10) {
            toast.error("Enter a valid 10-digit mobile number");
            return;
        }
        setLoading(true);
        const profile = saveVisitor(form);
        try {
            await api.post("/leads", {
                intent,
                name: form.name.trim(),
                phone,
                email: form.email.trim(),
                city: form.city.trim(),
                purpose: form.purpose,
                bike_id: bike?.id || "",
                bike_name: bike?.name || "",
                note: note || "",
                lat: coords?.lat ?? profile?.lat,
                lng: coords?.lng ?? profile?.lng,
            });
            trackLeadConversion(settings);
            toast.success("Thanks! Our team will call you shortly.");
            onOpenChange(false);
        } catch {
            toast.error("Failed to submit. Try WhatsApp instead.");
        } finally {
            setLoading(false);
        }
    };

    const waDigits = resolveWhatsAppDigits(settings);
    const waText = note
        ? (note.startsWith("Hi ") ? note : `Hi Servall Bikes, ${note}`)
        : bikeInquiryMessage(bike, intent === "loan_inquiry" ? "loan_inquiry" : intent);
    const waHref = whatsappUrl(waDigits, waText);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-2xl" data-testid="lead-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{TITLES[intent] || TITLES.general}</DialogTitle>
                    <DialogDescription>
                        {bike ? <>For <strong>{bike.name}</strong> — </> : null}
                        Name, phone &amp; why you're here. We'll call within 30 minutes during shop hours.
                    </DialogDescription>
                </DialogHeader>
                {note ? (
                    <p className="text-xs bg-[#fff5f5] text-[#4a4a52] rounded-lg px-3 py-2" data-testid="lead-note-preview">
                        {note}
                    </p>
                ) : null}
                <form onSubmit={submit} className="space-y-3 mt-1">
                    <div>
                        <label className="label">Name *</label>
                        <input
                            className="input"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Your full name"
                            autoComplete="name"
                            data-testid="lead-name"
                        />
                    </div>
                    <div>
                        <label className="label">Mobile *</label>
                        <input
                            className="input"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="10-digit mobile number"
                            inputMode="tel"
                            autoComplete="tel"
                            data-testid="lead-phone"
                        />
                    </div>
                    <div>
                        <label className="label">Why are you visiting? *</label>
                        <select
                            className="input"
                            value={form.purpose}
                            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                            data-testid="lead-purpose"
                        >
                            {VISIT_PURPOSES.map((p) => selectOption(p.value, p.label))}
                        </select>
                    </div>
                    {showMore ? (
                        <div className="space-y-3 pt-1 border-t border-zinc-100">
                            <div>
                                <label className="label">Email (optional)</label>
                                <input
                                    className="input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="you@email.com"
                                    data-testid="lead-email"
                                />
                            </div>
                            <div>
                                <label className="label">City (optional)</label>
                                <input
                                    className="input"
                                    value={form.city}
                                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                                    placeholder="e.g. Ahmedabad"
                                    data-testid="lead-city"
                                />
                            </div>
                            <div>
                                <label className="label">Location (optional)</label>
                                <button
                                    type="button"
                                    onClick={useLocation}
                                    disabled={locating}
                                    className="input w-full flex items-center justify-center gap-2 py-2.5 hover:border-[#ED1C24] transition-colors"
                                    data-testid="lead-use-location"
                                >
                                    {locating ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                                    {coords ? "Location saved ✓" : "Use my location"}
                                </button>
                                <p className="text-[10px] text-[#7a7a84] mt-1">Helps us suggest the nearest showroom.</p>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowMore(true)}
                            className="text-xs text-[#7a7a84] hover:text-[#ED1C24] underline-offset-2 hover:underline"
                            data-testid="lead-show-more"
                        >
                            + More (email, city, location)
                        </button>
                    )}
                    <button type="submit" disabled={loading} className="btn btn-primary w-full justify-center" data-testid="lead-submit">
                        {loading ? "Submitting…" : "Request Callback"}
                    </button>
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[#25D366] hover:bg-[#1ea952] text-white font-heading font-semibold text-sm transition-colors"
                        data-testid="lead-whatsapp"
                        onClick={() => onOpenChange(false)}
                    >
                        <MessageCircle size={16} /> Prefer WhatsApp instead
                    </a>
                    <p className="text-xs text-[#7a7a84] text-center pt-0.5">
                        By submitting you agree to our{" "}
                        <Link to="/privacy-policy" className="text-[#ED1C24] hover:underline" onClick={() => onOpenChange(false)}>
                            Privacy Policy
                        </Link>.
                    </p>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default LeadModal;
